import { ReactQuery } from 'cms-api'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { EXPERT_USER_CAPABILITIES, ROUTE } from 'shared-constants'
import styled from 'styled-components'
import { getLayoutWithExpertSideNav } from '@expert/components/ExpertLayout'
import { Box, Flex } from 'components/Layout'
import { SideNavPage } from 'components/SideNavPage'
import { ShopNavSection } from 'components/SideNavPage/ShopNavSection'
import { Tabs } from 'components/Tabs'
import { Text } from 'components/Typography'
import { useUserAuth } from 'providers/userAuth'
import { SPACE } from 'Theme'
import { TradeTierBadge } from '../../components/TradeTierBadge'
import { ConsultationsTabContent } from './ConsultationsTabContent'
import { TradeTabContent } from './TradeTabContent'

const DASHBOARD_TABS = {
  TRADE: { value: 'trade', label: 'Trade' },
  CONSULTATIONS: { value: 'consultations', label: 'Consultations' },
}

const StyledTab = styled(Tabs.Tab)`
  white-space: nowrap;
`

const getInitialTab = (queryTab) =>
  queryTab === DASHBOARD_TABS.CONSULTATIONS.value
    ? DASHBOARD_TABS.CONSULTATIONS.value
    : DASHBOARD_TABS.TRADE.value

const ExpertDashboardContent = () => {
  const { user } = useUserAuth()
  const router = useRouter()
  const { capabilities } = user
  const isConsultationCapable = capabilities.includes(
    EXPERT_USER_CAPABILITIES.CONSULTATION
  )
  const { data, isLoading } = ReactQuery.useGetExpertDashboardTradeTier()

  const [selectedTab, setSelectedTab] = useState(
    getInitialTab(router?.query?.tab)
  )

  useEffect(() => {
    setSelectedTab(getInitialTab(router?.query?.tab))
  }, [router.query])

  const tier = data?.currentTier ?? ''
  const businessName = user?.expert?.businessName ?? ''

  const today = new Date()
  const currentYear = today.getFullYear()
  const weekday = today.toLocaleDateString('en-US', { weekday: 'long' })
  const monthDayYear = today.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
  const formattedDate = `${weekday} · ${monthDayYear}`
  const startOfNextYear = new Date(currentYear + 1, 0, 1)
  const daysLeftInYear = Math.max(
    0,
    Math.ceil((startOfNextYear - today) / (1000 * 60 * 60 * 24))
  )
  const incentive = data?.nextTier
    ? `to level up to ${data.nextTier}`
    : `to maintain ${tier || 'your status'}`

  return (
    <>
      <SideNavPage.Header
        height="auto"
        leftElement={() => (
          <Box display={{ MOBILE: 'none', TABLET: 'block' }}>
            <SideNavPage.Title>
              {user?.expertFirstName
                ? `Welcome back, ${user.expertFirstName}!`
                : 'Dashboard'}
            </SideNavPage.Title>
            {businessName && (
              <Box mt={SPACE.PX_4}>
                <Text fontSize={13} fontWeight={600} color="textSecondary">
                  {businessName}
                </Text>
              </Box>
            )}
            <Box mt={SPACE.PX_8}>
              <Text fontSize={13} color="textSecondary">
                {formattedDate} ·{' '}
                <Text as="span" fontWeight={600}>
                  {daysLeftInYear} days
                </Text>{' '}
                left in {currentYear} {incentive}
              </Text>
            </Box>
            <Box mt={SPACE.PX_20}>
              <Link href={`${ROUTE.TRADE()}#benefits`}>
                <Text
                  fontSize={9}
                  fontWeight={600}
                  letterSpacing="0.08em"
                  style={{ textTransform: 'uppercase', cursor: 'pointer' }}
                >
                  Your firm is earning status shopping from our family of brands
                  — learn more
                </Text>
              </Link>
            </Box>
          </Box>
        )}
        rightElement={() => (
          <Flex alignItems="center" gap={12}>
            {tier && (
              <Box display={{ MOBILE: 'none', TABLET: 'block' }}>
                <Link href={`${ROUTE.TRADE()}#benefits`}>
                  <TradeTierBadge tier={tier} year={currentYear} />
                </Link>
              </Box>
            )}
            <ShopNavSection />
          </Flex>
        )}
      />
      <SideNavPage.Content
        pt={isConsultationCapable ? { TABLET: 0 } : undefined}
      >
        <Box display={{ MOBILE: 'block', TABLET: 'none' }} mb={SPACE.PX_20}>
          <Flex gap={14} alignItems="center">
            <Text fontSize={20} fontWeight={500}>
              {user?.expertFirstName
                ? `Welcome back, ${user.expertFirstName}!`
                : 'Dashboard'}
            </Text>
            {tier && (
              <Link href={`${ROUTE.TRADE()}#benefits`}>
                <TradeTierBadge tier={tier} year={currentYear} />
              </Link>
            )}
          </Flex>
        </Box>

        {isConsultationCapable ? (
          <Tabs.Root
            value={selectedTab}
            onValueChange={(value) => {
              router.push(`${router.pathname}?tab=${value}`, undefined, {
                shallow: true,
              })
            }}
          >
            <Box mb={SPACE.PX_20}>
              <Tabs.List aria-label="Dashboard sections">
                <Flex width="fit-content">
                  {Object.values(DASHBOARD_TABS).map((tab) => (
                    <StyledTab value={tab.value} key={tab.value}>
                      {tab.label}
                    </StyledTab>
                  ))}
                </Flex>
              </Tabs.List>
            </Box>
            <Tabs.Content
              value={DASHBOARD_TABS.TRADE.value}
              key={DASHBOARD_TABS.TRADE.value}
            >
              <TradeTabContent isLoading={isLoading} data={data} />
            </Tabs.Content>
            <Tabs.Content
              value={DASHBOARD_TABS.CONSULTATIONS.value}
              key={DASHBOARD_TABS.CONSULTATIONS.value}
            >
              <ConsultationsTabContent />
            </Tabs.Content>
          </Tabs.Root>
        ) : (
          <TradeTabContent isLoading={isLoading} data={data} />
        )}
      </SideNavPage.Content>
    </>
  )
}

ExpertDashboardContent.getLayout = getLayoutWithExpertSideNav

export const ExpertDashboard = ExpertDashboardContent
